import React, { useEffect, useMemo, useState } from "react";

import { faTrashAlt } from "@fortawesome/pro-duotone-svg-icons";
import {
  faCompressAlt,
  faExpandAlt,
  faPlus,
  faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { setSetting } from "actions";

import { AttachmentForm } from "components/AttachmentForm";
import { ConfirmBrandUpdateDialog } from "components/AttachmentForm/ConfirmBrandUpdateDialog";
import { MultiButton } from "components/Button";
import Clickable from "components/Button/Clickable";
import Dialog from "components/Dialog";
import { Button } from "components/Form/Button";
import { Column, Row } from "components/Layout";
import { Paper } from "components/Paper";
import { UploadModal } from "components/UploadModal";

import { AttachmentSearchFields } from "constants/attachmentSearch";
import { NVDRelatedDocumentTypeValues } from "constants/documentTypes";
import { Settings } from "constants/settings";

import { CurrentAgencySelector } from "containers/ConnectedSelector";

import { compareAttachments } from "lib/compare";
import { getLivestockSaleId } from "lib/navigation";

import {
  currentSaleSelector,
  getConsignments,
  getFiles,
  getProperties,
  getSetting,
  selectDeploymentSaleIdByLivestockSaleIdAndAgencyId,
  selectIsCompleteByAttachmentIdLookup,
} from "selectors";

import {
  getCurrentAgenciesOptions,
  selectAgencyIdByDeploymentSaleIdLookup,
} from "selectors/agencies";

import {
  useHasAddAttachmentWithoutImagePermission,
  useMountEffect,
} from "hooks";
import { useIsMobile } from "hooks/useScreenSize";

import { AttachmentSearch } from "./AttachmentSearch";
import AttachmentThumb from "./AttachmentThumb";
import MobileImageViewer from "./MobileImageViewer";
import { Preview } from "./Preview";

const Wrapper = styled(Column)`
  ${({ theme }) => `
  flex: 1;
  overflow-y: hidden;
  height: calc(100vh - 63px - ${theme.space[2] * 2}px);
`}
`;

const ModalWrapper = styled(Column)`
  background-color: #fff;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`;

const AddAttachmentContainer = styled.div`
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
  display: flex;
  align-items: center;
`;

const MobileButton = styled(Clickable)`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 24px;
  ${({ theme, hoverRed }) =>
    hoverRed && `&:hover{ color: ${theme.colors.inputError} }`};
`;

const CloseModalButton = styled(Clickable)`
  position: fixed;
  right: 36px;
  bottom: 36px;
  font-size: 24px;
  color: #666;
  line-height: 0;
  padding: 12px;
  border-radius: 100px;
  background-color: #fff;
`;

const ThumbnailRow = styled.div`
  position: relative;
  min-height: 78px;
  border-bottom: 1px solid #bababa;
`;

const ThumbnailRowInner = styled(Row)`
  padding: 0 12px;
  margin-right: 72px;
  overflow-x: auto;
`;

const ThumbnailColumn = styled(Column)`
  flex: 1;
  overflow-y: auto;
  align-items: center;
`;

const MobileControls = styled(Row)`
  justify-content: space-between;
  padding: 24px;
  ${({ disabled }) => {
    if (disabled) {
      return `opacity: 0.33;
              pointer-events: none;`;
    }
  }};
`;

const FilterBar = styled(Row)`
  ${({ theme }) => `
  padding: ${theme.space[1]}px ${theme.space[2]}px;
  gap: ${theme.space[2]}px;
  align-items: center;
`}
`;

const AgencySelectorWrapper = styled.div`
  flex-shrink: 0;
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const CloseButton = styled(Clickable)(
  ({ theme }) => `
  font-size: ${theme.fontSizes.delta}px;
`,
);

const DesktopGridLayout = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: grid;
  grid-gap: ${theme.space[2]}px;
  margin: 0 ${theme.space[2]}px ${theme.space[2]}px;
  grid-template-columns: min-content auto 328px;
  min-height: 0;
  > div {
    overflow: hidden;
  }
`}
`;

const FormWrapper = styled(Paper)`
  position: relative;
  min-height: 0;
  overflow: hidden;
`;

function getFilteredAttachments(
  files,
  agencyId,
  deploymentSaleId,
  attachmentSearchFilters,
  consignments,
  properties,
  isCompleteByAttachmentIdLookup,
) {
  const nvdRelatedFiles = Object.values(files).filter(
    file => NVDRelatedDocumentTypeValues.includes(file.type) || !file.type,
  );

  return nvdRelatedFiles
    .filter(file => {
      // Agency / deployment sale filter
      if (agencyId && deploymentSaleId) {
        if (file.deploymentSale && file.deploymentSale !== deploymentSaleId) {
          return false;
        }
      }

      // Attachment search filters
      if (attachmentSearchFilters) {
        for (const [field, values] of Object.entries(attachmentSearchFilters)) {
          if (!values || values.length === 0) {
            continue;
          }

          if (field === AttachmentSearchFields.Type) {
            if (!values.includes(file.type)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.Consignment) {
            if (!file.consignment || !values.includes(file.consignment)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.SerialNumber) {
            if (
              !file.envd?.serialNumber ||
              !values.includes(file.envd.serialNumber)
            ) {
              return false;
            }
          } else if (field === AttachmentSearchFields.VendorPIC) {
            if (!file.consignment) {
              return false;
            }
            const consignment = consignments[file.consignment];
            const property =
              consignment && properties[consignment.vendor_property_id];
            if (!property?.PIC || !values.includes(property.PIC)) {
              return false;
            }
          } else if (field === AttachmentSearchFields.AdditionalPICs) {
            if (!file.consignment) {
              return false;
            }
            const consignment = consignments[file.consignment];
            const additionalPICs = (
              consignment?.additional_properties || []
            ).map(p => p?.PIC);
            if (!values.some(v => additionalPICs.includes(v))) {
              return false;
            }
          } else if (field === AttachmentSearchFields.NVDVersion) {
            if (!file.consignment) {
              return false;
            }
            const consignment = consignments[file.consignment];
            if (
              !consignment?.declaration?.VERSION ||
              !values.includes(consignment.declaration.VERSION)
            ) {
              return false;
            }
          } else if (field === AttachmentSearchFields.IsComplete) {
            const isComplete = isCompleteByAttachmentIdLookup[file.id] || false;
            if (!values.includes(isComplete)) {
              return false;
            }
          }
        }
      }

      return true;
    })
    .sort(compareAttachments);
}

const AttachmentsView = ({
  setAdditionalPICs,
  updateDeclaration,
  redirectToConsignments,
  updateAttachment,
  uploadRequest,
  createPlaceholderAttachment,
  deleteAttachment,
  patchConsignment,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const sale = useSelector(currentSaleSelector);
  const isCompleteByAttachmentIdLookup = useSelector(
    selectIsCompleteByAttachmentIdLookup,
  );

  const hasAddAttachmentWithoutImagePermission =
    useHasAddAttachmentWithoutImagePermission();

  const files = useSelector(getFiles);
  const consignments = useSelector(getConsignments);
  const properties = useSelector(getProperties);

  // Read agency from Redux setting
  const agencyId = useSelector(getSetting(Settings.agencyId));
  const agencyOptions = useSelector(getCurrentAgenciesOptions(false));

  const deploymentSaleId = useSelector(state =>
    selectDeploymentSaleIdByLivestockSaleIdAndAgencyId(
      state,
      getLivestockSaleId(),
      agencyId,
    ),
  );

  // Read attachment search filters from Redux setting
  const attachmentSearchFilters = useSelector(
    getSetting(Settings.attachmentSearch),
  );

  const selectedAttachmentIdSetting = useSelector(
    getSetting(Settings.selectedAttachmentId),
  );

  const filteredAttachments = useMemo(
    () =>
      getFilteredAttachments(
        files,
        agencyId,
        deploymentSaleId,
        attachmentSearchFilters,
        consignments,
        properties,
        isCompleteByAttachmentIdLookup,
      ),
    [
      files,
      agencyId,
      deploymentSaleId,
      attachmentSearchFilters,
      consignments,
      properties,
      isCompleteByAttachmentIdLookup,
    ],
  );

  const [selectedAttachmentId, setSelectedAttachmentId] = useState(
    selectedAttachmentIdSetting ||
      (filteredAttachments[0] ? filteredAttachments[0].id : null),
  );

  // Clear one-shot selected attachment on mount; clear all filters on unmount
  useMountEffect(() => {
    if (selectedAttachmentIdSetting) {
      dispatch(setSetting(Settings.selectedAttachmentId, null));
    }
    return () => {
      dispatch(setSetting(Settings.attachmentSearch, null));
      dispatch(setSetting(Settings.selectedAttachmentId, null));
    };
  });

  const [confirmBrandUpdateDialogProps, setConfirmBrandUpdateDialogProps] =
    useState(null);

  useEffect(() => {
    if (
      !selectedAttachmentId ||
      !filteredAttachments.find(a => a.id === selectedAttachmentId)
    ) {
      setSelectedAttachmentId(
        filteredAttachments[0] ? filteredAttachments[0].id : null,
      );
    }
  }, [filteredAttachments, selectedAttachmentId]);

  const [isImageModal, setIsImageModal] = useState(false);
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [isDeleteDialog, setIsDeleteDialog] = useState(false);

  const selectedAttachment = useMemo(
    () =>
      filteredAttachments.find(
        attachment => attachment.id === selectedAttachmentId,
      ),
    [filteredAttachments, selectedAttachmentId],
  );

  const selectedAttachmentUrl = selectedAttachment
    ? selectedAttachment.image_url
    : "";

  const getNextAttachment = () => {
    const currentIndex = filteredAttachments.findIndex(
      a => a.id === selectedAttachmentId,
    );
    const nextAttachment = filteredAttachments[currentIndex + 1];
    if (nextAttachment) {
      setSelectedAttachmentId(nextAttachment.id);
    }
  };

  const deleteSelectedAttachment = () => {
    const attachment = filteredAttachments.find(
      attachment => attachment.id === selectedAttachmentId,
    );
    selectedAttachmentId && deleteAttachment(attachment) && getNextAttachment();
  };

  const replaceSelectedAttachment = (attachment, newFile) => {
    typeof updateAttachment === "function" &&
      updateAttachment({ ...attachment, file: newFile });
  };

  const agencyIdByDeploymentSaleId = useSelector(
    selectAgencyIdByDeploymentSaleIdLookup,
  );

  // Derive consignment filter state
  const consignmentFilter =
    attachmentSearchFilters?.[AttachmentSearchFields.Consignment];
  const selectedConsignmentIdFromFilter =
    consignmentFilter?.length === 1 ? consignmentFilter[0] : null;
  const hasMultipleConsignments = consignmentFilter?.length > 1;

  // Determine effective agency: use selected agency, or auto-use if only one option
  const hasAgencyFilter = Boolean(agencyId);
  const onlyAgencyId =
    agencyOptions.length === 1 ? agencyOptions[0].value : null;
  const effectiveAgencyId = agencyId || onlyAgencyId;

  // Check for agency/consignment conflict
  const selectedConsignment = selectedConsignmentIdFromFilter
    ? consignments[selectedConsignmentIdFromFilter]
    : null;
  const consignmentAgencyId = selectedConsignment
    ? agencyIdByDeploymentSaleId[selectedConsignment.deployment_sale]
    : null;
  const hasAgencyConflict =
    hasAgencyFilter &&
    selectedConsignmentIdFromFilter &&
    consignmentAgencyId &&
    consignmentAgencyId !== agencyId;

  const handleUpload = file => {
    const formData = { sale };
    if (selectedConsignmentIdFromFilter) {
      formData.consignment = selectedConsignmentIdFromFilter;
    }
    if (effectiveAgencyId) {
      formData.agency = effectiveAgencyId;
    }
    uploadRequest(file, formData);
    setIsUploadModal(false);
  };

  const thumbnails = filteredAttachments.map(attachment => {
    return (
      <AttachmentThumb
        key={attachment.id}
        id={attachment.id}
        progress={attachment.progress}
        parseResult={attachment.latest_parse_result_status}
        thumbUrl={attachment.thumbnail_url}
        selected={selectedAttachmentId === attachment.id}
        setSelected={setSelectedAttachmentId}
        isComplete={isCompleteByAttachmentIdLookup[attachment.id]}
        isMobile={isMobile}
        isEnvd={Boolean(attachment.envd)}
      />
    );
  });

  const DeleteDialog = () =>
    isDeleteDialog && (
      <Dialog
        closeSelf={() => setIsDeleteDialog(false)}
        handleSubmit={deleteSelectedAttachment}
        title="Permanently delete document?"
        subtitle="Are you sure you want to permanently delete the file?"
        warningText="The document data entered will also be deleted"
        submitButtonText="Delete Document"
        rejectButtonText="Keep"
        icon={faTrashAlt}
      />
    );

  const UploadFileModal = () =>
    isUploadModal && (
      <UploadModal
        closeSelf={() => setIsUploadModal(false)}
        handleUpload={handleUpload}
      />
    );

  const handleGoBack = () => redirectToConsignments();

  const handleShowUploadModal = () => setIsUploadModal(true);

  const handleCreateEmptyAttachment = () => {
    const formData = { sale };
    if (selectedConsignmentIdFromFilter) {
      formData.consignment = selectedConsignmentIdFromFilter;
    }
    if (effectiveAgencyId) {
      formData.agency = effectiveAgencyId;
    }
    createPlaceholderAttachment(formData);
  };

  // Build a label describing what the new attachment will be created for
  const effectiveAgencyLabel = effectiveAgencyId
    ? agencyOptions.find(o => o.value === effectiveAgencyId)?.label
    : null;
  const addContext = selectedConsignmentIdFromFilter
    ? "consignment"
    : effectiveAgencyLabel
      ? effectiveAgencyLabel
      : null;

  // Enable add when we have enough context: an agency or a single consignment
  // Disable when multiple consignments filtered, or agency/consignment conflict
  const canAdd =
    !hasMultipleConsignments &&
    !hasAgencyConflict &&
    (hasAgencyFilter || onlyAgencyId || selectedConsignmentIdFromFilter);
  const addDisabledTooltip = hasAgencyConflict
    ? "Selected consignment does not belong to the selected agency"
    : !canAdd
      ? "Select an agency or a single consignment to add attachments"
      : undefined;

  const addTooltip =
    addDisabledTooltip || (addContext ? `Adding to ${addContext}` : undefined);

  const attachmentActions = [
    {
      title: isMobile ? null : "Add",
      isDisabled: !canAdd,
      tooltip: addTooltip,
      onClick: handleShowUploadModal,
      default: true,
      icon: faPlus,
      dataTour: "addAttachment",
    },
    {
      title: "Add without image",
      isDisabled: !hasAddAttachmentWithoutImagePermission || !canAdd,
      tooltip: addTooltip,
      onClick: handleCreateEmptyAttachment,
      default: false,
      icon: faPlus,
      dataTour: "addAttachmentWithoutImage",
    },
  ];

  if (isMobile) {
    const thumbnailsAndAddNew = (
      <ThumbnailRow>
        <ThumbnailRowInner alignCenter>{thumbnails}</ThumbnailRowInner>
        <AddAttachmentContainer>
          {hasAddAttachmentWithoutImagePermission ? (
            <MultiButton buttons={attachmentActions} />
          ) : (
            <Button
              onClick={handleShowUploadModal}
              disabled={!canAdd}
              title={addTooltip}
              minWidth="2.5rem"
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          )}
        </AddAttachmentContainer>
      </ThumbnailRow>
    );

    if (isImageModal) {
      return (
        <ModalWrapper>
          {thumbnailsAndAddNew}

          <MobileImageViewer
            imageUrl={selectedAttachmentUrl}
            style={{ flexGrow: 1 }}
          />

          <CloseModalButton onClick={() => setIsImageModal(false)}>
            <FontAwesomeIcon icon={faCompressAlt} />
          </CloseModalButton>
        </ModalWrapper>
      );
    }

    return (
      <Wrapper isMobile>
        <FilterBar style={{ flexDirection: "column", alignItems: "stretch" }}>
          <Row alignCenter justifyBetween>
            <AgencySelectorWrapper>
              <CurrentAgencySelector includeAll />
            </AgencySelectorWrapper>
            <CloseButton onClick={handleGoBack}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </Row>
          <SearchWrapper>
            <AttachmentSearch />
          </SearchWrapper>
        </FilterBar>
        {thumbnailsAndAddNew}
        <MobileControls disabled={selectedAttachmentId === null}>
          <MobileButton onClick={() => setIsImageModal(true)}>
            <FontAwesomeIcon icon={faExpandAlt} />
            <span style={{ fontSize: 14, paddingLeft: 12 }}>View document</span>
          </MobileButton>
          <MobileButton hoverRed onClick={() => setIsDeleteDialog(true)}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </MobileButton>
        </MobileControls>
        <FormWrapper>
          <AttachmentForm
            key={selectedAttachmentId}
            updateDeclaration={updateDeclaration}
            setAdditionalPICs={setAdditionalPICs}
            selectedAttachment={selectedAttachment}
            updateAttachment={updateAttachment}
            patchConsignment={patchConsignment}
            getNextAttachment={getNextAttachment}
            handleGoBack={handleGoBack}
          />
        </FormWrapper>
        <DeleteDialog />
        <UploadFileModal />
        <ConfirmBrandUpdateDialog
          confirmBrandUpdateDialogProps={confirmBrandUpdateDialogProps}
          setConfirmBrandUpdateDialogProps={setConfirmBrandUpdateDialogProps}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FilterBar>
        <AgencySelectorWrapper>
          <CurrentAgencySelector includeAll />
        </AgencySelectorWrapper>
        <SearchWrapper>
          <AttachmentSearch />
        </SearchWrapper>
        <CloseButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </FilterBar>
      <DesktopGridLayout>
        <Paper spacing={1} style={{ minWidth: 108 }}>
          <ThumbnailColumn>{thumbnails}</ThumbnailColumn>
          <Row justifyCenter style={{ marginTop: 12, marginBottom: 6 }}>
            {hasAddAttachmentWithoutImagePermission ? (
              <MultiButton
                buttons={attachmentActions}
                popoverHorizontal="left"
              />
            ) : (
              <Button
                onClick={handleShowUploadModal}
                disabled={!canAdd}
                title={addTooltip}
              >
                Add <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
          </Row>
        </Paper>

        <Preview
          file={selectedAttachment}
          handleDelete={deleteSelectedAttachment}
          handleReplace={replaceSelectedAttachment}
        />

        <FormWrapper>
          <AttachmentForm
            key={selectedAttachmentId}
            updateDeclaration={updateDeclaration}
            setAdditionalPICs={setAdditionalPICs}
            selectedAttachment={selectedAttachment}
            patchConsignment={patchConsignment}
            updateAttachment={updateAttachment}
            getNextAttachment={getNextAttachment}
            handleGoBack={handleGoBack}
            setConfirmBrandUpdateDialogProps={setConfirmBrandUpdateDialogProps}
          />
        </FormWrapper>
      </DesktopGridLayout>
      <UploadFileModal />
      <ConfirmBrandUpdateDialog
        confirmBrandUpdateDialogProps={confirmBrandUpdateDialogProps}
        setConfirmBrandUpdateDialogProps={setConfirmBrandUpdateDialogProps}
      />
    </Wrapper>
  );
};

AttachmentsView.propTypes = {
  updateAttachment: PropTypes.func.isRequired,
  deleteAttachment: PropTypes.func.isRequired,
  setAdditionalPICs: PropTypes.func.isRequired,
  updateDeclaration: PropTypes.func.isRequired,
  patchConsignment: PropTypes.func.isRequired,
  uploadRequest: PropTypes.func.isRequired,
  redirectToConsignments: PropTypes.func.isRequired,
};

export default AttachmentsView;
